import React, { useEffect, useState } from 'react'
import { useStyles } from './TemplateCardStyling.styles'
import { SelectChangeEvent, Typography } from '@mui/material'
import {
  ColorSelectorV2,
  SelectV2,
  SwitchV2,
  SwitchVarient,
  TextInputV2,
} from '@labrav/react-components'
import { useFormikContext } from 'formik'
import clsx from 'clsx'
import {
  extractColorV2,
  hexadecimalColor,
} from '../../../../common/utils/productCreationUtil'
import { convertRGBAAlpha } from '../../../../common/utils/convertRGBAAlpha'
import { calculateMinMaxResult } from '../../../AddEditProductFormComponents/FlyOutProductBranding/FlyOutProductBranding'
import { useStyles as commonStyles } from '../../FlyOutStorefront.styles'
import { TemplateInCreation } from '../../../../modules/storefront/templateCreation/reducer'
import {
  borderStrokeWidthSizes,
  fontNames,
  fontSizes,
} from '../../../../utils/templateCreationConstants'
import { isBoolean } from 'lodash'
import {
  DataType,
  FormField,
} from '@labrav/react-components/lib/@types/types/forms'
import { hexToRgb, rgbToHex } from '../../../../utils/colorsConvertor'
import { ListingCardInCreation } from '../../../../modules/storefront/listingCardCreation/reducer'

export const TemplateCardStyling: React.FC = () => {
  const { classes } = useStyles()
  const { classes: commonClasses } = commonStyles()
  const { values, errors, touched, setFieldValue, handleBlur, handleChange } =
    useFormikContext<ListingCardInCreation>()
  const { listingCardData } = values

  // Bg Color
  const [bgColorType, setBgColorType] = useState<string>(
    listingCardData?.template?.data?.card?.customization?.backgroundColor
      ? listingCardData?.template?.data?.card?.customization?.backgroundColor?.includes(
          '#'
        )
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )
  const [headerBgColor, setHeaderBgColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.card?.customization?.backgroundColor ||
      ''
    return extractColorV2(bgColorType, colorString)
  })
  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.card?.customization?.backgroundColor ||
      ''
    setHeaderBgColor(extractColorV2(bgColorType, colorString))
  }, [listingCardData?.template?.data?.card?.customization?.backgroundColor])

  // Border Color
  const [borderColorType, setBorderColorType] = useState<string>(
    listingCardData?.template?.data?.card?.customization?.borderColor
      ? listingCardData?.template?.data?.card?.customization?.borderColor?.includes(
          '#'
        )
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )
  const [headerBorderColor, setHeaderBorderColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.card?.customization?.borderColor || ''
    return extractColorV2(borderColorType, colorString)
  })
  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.card?.customization?.borderColor || ''
    setHeaderBorderColor(extractColorV2(borderColorType, colorString))
  }, [listingCardData?.template?.data?.card?.customization?.borderColor])

  const handleColorChange = async (
    type: string,
    index: number,
    event: SelectChangeEvent
  ) => {
    const updatedColorValues = calculateMinMaxResult(
      index,
      type === 'background' ? [...headerBgColor] : [...headerBorderColor],
      event.target.value as string
    )

    const color =
      type === 'background'
        ? bgColorType === 'RGB'
          ? `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, 100)`
          : hexadecimalColor(`${updatedColorValues[4]}`, 100)
        : borderColorType === 'RGB'
          ? `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, 100)`
          : hexadecimalColor(`${updatedColorValues[4]}`, 100)

    const field =
      type === 'background'
        ? 'listingCardData.template.data.card.customization.backgroundColor'
        : 'listingCardData.template.data.card.customization.borderColor'

    await setFieldValue(field, color)
  }

  const handleTypeChange = async (type: string, event: SelectChangeEvent) => {
    const typeValue = event.target.value as string
    if (type === 'background') {
      const resetValue =
        typeValue === 'RGB'
          ? hexToRgb(headerBgColor[4]).toString()
          : rgbToHex(
              parseInt(headerBgColor[0]),
              parseInt(headerBgColor[1]),
              parseInt(headerBgColor[2])
            )
      setBgColorType(typeValue)
      await setFieldValue(
        'listingCardData.template.data.card.customization.backgroundColor',
        resetValue
      )
    } else if (type === 'border') {
      const resetValue =
        typeValue === 'RGB'
          ? hexToRgb(headerBorderColor[4]).toString()
          : rgbToHex(
              parseInt(headerBorderColor[0]),
              parseInt(headerBorderColor[1]),
              parseInt(headerBorderColor[2])
            )
      setBorderColorType(typeValue)
      await setFieldValue(
        'listingCardData.template.data.card.customization.borderColor',
        resetValue
      )
    }
  }

  // Card Shadow
  const isCardShadow = isBoolean(
    values?.listingCardData?.template?.data?.card?.customization?.cardShadow
  )
    ? values?.listingCardData?.template?.data?.card?.customization?.cardShadow
    : false
  const handleChangeCardShadow = (e: {
    target: {
      checked: boolean
    }
  }) => {
    setFieldValue(
      'listingCardData.template.data.card.customization.cardShadow',
      e?.target?.checked
    )
  }
  const [inputFont, setInputFont] = useState(
    values?.listingCardData?.template?.data?.card?.customization.font
  )
  useEffect(() => {
    if (!fontNames.includes(inputFont as string)) {
      setInputFont('other')
    }
  }, [])

  const handleFontChange = (e: SelectChangeEvent) => {
    const selectedFont = e.target.value as string
    if (selectedFont === 'other') {
      setInputFont('other')
      setFieldValue('listingCardData.template.data.card.customization.font', '')
    } else {
      setInputFont(selectedFont)
      setFieldValue(
        'listingCardData.template.data.card.customization.font',
        selectedFont
      )
    }
  }

  const fontNamesFields: FormField = {
    title: 'Select font family',
    placeHolder: 'Select font family',
    id: 'listingCardData.template.data.card.customization.font',
    isRequired: false,
    humanReadableKey: 'selectFontSize',
    isCustom: false,
    defaultValue: inputFont,
    dataType: 'enum' as DataType,
    options: fontNames,
    isReadOnly: false,
  }

  return (
    <div
      className={clsx(
        classes.sectionWrapper,
        classes.sectionHeaderWrapper,
        classes.content,
        classes.root
      )}
      data-testid="card-styling-component"
    >
      <div className={classes.textContentWrapper}>
        <Typography
          className={classes.textHeading}
          data-testid="card-styling-text"
        >
          Cards styling
        </Typography>
      </div>
      <div
        className={classes.sectionInnerWrapper}
        data-testid="card-styling-inner-component"
      >
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="card-styling-font"
          >
            Font
          </Typography>
          <div
            className={classes.textInput}
            data-testid="card-style-font-input"
          >
            <SelectV2
              field={{ ...fontNamesFields }}
              value={inputFont}
              onChange={handleFontChange}
              onBlur={handleBlur}
            />
          </div>
        </div>
        {inputFont === 'other' && (
          <div className={classes.rowContainer}>
            <div className={classes.sectionTitle}></div>
            <div
              className={classes.textInput}
              data-testid="card-style-font-input"
            >
              {
                <TextInputV2
                  keyName="listingCardData.template.data.card.customization.font"
                  title="Font family"
                  type="text"
                  placeHolder=""
                  value={
                    listingCardData?.template?.data?.card?.customization
                      ?.font || ''
                  }
                  isError={
                    !!(
                      (touched as any).listingCardData?.template?.data?.card
                        ?.customization?.font &&
                      (errors as any).listingCardData.template.data?.card
                        ?.customization?.font
                    )
                  }
                  onChange={handleChange}
                  updatedValue={
                    listingCardData?.template?.data?.card?.customization
                      ?.font || ''
                  }
                  isReadOnly={false}
                  isRequired={false}
                  errorText={
                    !!(touched as any).listingCardData?.template?.data?.card
                      ?.customization?.font &&
                    ((errors as any).listingCardData?.template?.data?.card
                      ?.customization?.font ||
                      '')
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              }
            </div>
          </div>
        )}

        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="card-styling-bg-color"
          >
            Background color
          </Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              data-testid="card-style-bg-color-selector"
              selectedField={bgColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              onChange={event => handleTypeChange('background', event)}
              keyName={
                'listingCardData.template.data.card.customization.backgroundColor'
              }
              colorValues={headerBgColor}
              handleColorChange={(index, event) =>
                handleColorChange('background', index, event)
              }
              color={convertRGBAAlpha(
                listingCardData?.template?.data?.card?.customization
                  ?.backgroundColor || '',
                false
              )}
              isError={
                !!(
                  (touched as any).listingCardData?.template?.data?.card
                    ?.customization?.backgroundColor &&
                  (errors as any).listingCardData?.template?.data?.card
                    ?.customization?.backgroundColor
                )
              }
              errorText={
                !!(touched as any).listingCardData?.template?.data?.card
                  ?.customization?.backgroundColor &&
                ((errors as any).listingCardData?.template?.data?.card
                  ?.customization?.backgroundColor ||
                  '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="card-styling-border-color"
          >
            Border color
          </Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              data-testid="card-style-border-color-selector"
              selectedField={borderColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              onChange={event => handleTypeChange('border', event)}
              keyName={
                'listingCardData.template.data.card.customization.borderColor'
              }
              colorValues={headerBorderColor}
              handleColorChange={(index, event) =>
                handleColorChange('border', index, event)
              }
              color={convertRGBAAlpha(
                listingCardData?.template?.data?.card?.customization
                  ?.borderColor || '',
                false
              )}
              isError={
                !!(
                  (touched as any).listingCardData?.template?.data?.card
                    ?.customization?.borderColor &&
                  (errors as any).listingCardData?.template?.data?.card
                    ?.customization?.borderColor
                )
              }
              errorText={
                !!(touched as any).listingCardData?.template?.data?.card
                  ?.customization?.borderColor &&
                ((errors as any).listingCardData?.template?.data?.card
                  ?.customization?.borderColor ||
                  '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="card-styling-broke-stroke-width"
          >
            Border stroke width
          </Typography>
          <div>
            <SelectV2
              data-testid="border-stroke-width-selector"
              minWidth="119px"
              field={{
                id: 'listingCardData.template.data.card.customization.borderStrokeWidth',
                isRequired: false,
                humanReadableKey: 'borderStrokeWidth',
                isReadOnly: false,
                isCustom: false,
                defaultValue: '1px',
                dataType: 'string',
                options: borderStrokeWidthSizes,
              }}
              value={
                listingCardData?.template?.data?.card?.customization
                  .borderStrokeWidth || '1px'
              }
              key={
                'listingCardData.template.data.card.customization.borderStrokeWidth'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              isError={false}
              errorText={''}
              isRequired={false}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="card-styling-rounded-corners"
          >
            Rounded corner radius
          </Typography>
          <div>
            <SelectV2
              data-testid="rounded-corners-selector"
              minWidth="119px"
              field={{
                id: 'listingCardData.template.data.card.customization.borderRadius',
                isRequired: false,
                humanReadableKey: 'borderRadius',
                isReadOnly: false,
                isCustom: false,
                defaultValue: '4px',
                dataType: 'string',
                options: fontSizes,
              }}
              value={
                listingCardData?.template?.data?.card?.customization
                  .borderRadius || '4px'
              }
              key={
                'listingCardData.template.data.card.customization.borderRadius'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              isError={false}
              errorText={''}
              isRequired={false}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="card-styling-card-shadow"
          >
            Card shadow
          </Typography>
          <SwitchV2
            data-testid="card-shadow-switch"
            checked={isCardShadow}
            switchVarient={SwitchVarient.DARK}
            onChange={handleChangeCardShadow}
          />
        </div>
      </div>
    </div>
  )
}
